import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  createNationality,
  getNationalityById,
  NATIONALITY_DESCRIPTION_MAX_LENGTH,
  NATIONALITY_NAME_MAX_LENGTH,
  updateNationality,
} from '../../services/nationalityMasters';
import { setFlashToast } from '../../utils/flashToast';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../utils/requiredValidation';
import { exceedsMax, maxCharLimitExceededError } from '../../utils/caseFieldValidation';

const FIELD_ERR_STYLE = { fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 } as const;

const READONLY_FIELD_STYLE = {
  background: 'color-mix(in srgb, var(--bg) 70%, var(--surface))',
  color: 'var(--t2)',
  cursor: 'not-allowed' as const,
};

export default function NationalityFormPage() {
  const { t } = useTranslation();
  const { nationalityId } = useParams();
  const isEdit = Boolean(nationalityId);
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!isEdit || !nationalityId) return;
    let alive = true;
    setLoading(true);
    void getNationalityById(nationalityId).then((res) => {
      if (!alive) return;
      if (res.success) {
        const d = res.data;
        setName(d.name);
        setCode(d.code);
        setDescription(d.description);
        setActive(d.active);
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, nationalityId, t]);

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      {
        key: 'name',
        label: t('metadata.nationalities.nationalityName'),
        value: name,
        elementId: 'nationality-name',
      },
    ];
  }, [name, t]);

  const validationSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      ...requiredSpecs,
      {
        key: 'description',
        label: t('metadata.nationalities.description'),
        value: description,
        elementId: 'nationality-description',
      },
    ];
  }, [description, requiredSpecs, t]);

  async function handleSave() {
    if (savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (exceedsMax(name, NATIONALITY_NAME_MAX_LENGTH)) {
      errs.name = maxCharLimitExceededError();
    }
    if (exceedsMax(description, NATIONALITY_DESCRIPTION_MAX_LENGTH)) {
      errs.description = maxCharLimitExceededError();
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(validationSpecs, errs);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        active,
      };
      const res =
        isEdit && nationalityId
          ? await updateNationality(nationalityId, payload)
          : await createNationality(payload);
      if (!res.success) {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
        return;
      }
      setFlashToast({
        message: isEdit
          ? t('metadata.nationalityForm.updateSuccess')
          : t('metadata.nationalityForm.createSuccess'),
        variant: 'success',
      });
      nav('/metadata/nationalities');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/nationalities">{t('metadata.nationalities.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('metadata.nationalityForm.updateTitle') : t('metadata.nationalityForm.addTitle')}
      </div>

      <div className="pg-title">
        {isEdit ? t('metadata.nationalityForm.updateTitle') : t('metadata.nationalityForm.addTitle')}
      </div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f">
              <div className="al-l">
                {t('metadata.nationalities.nationalityName')} <span className="req">*</span>
              </div>
              <input
                id="nationality-name"
                className="al-inp"
                value={name}
                maxLength={NATIONALITY_NAME_MAX_LENGTH}
                placeholder={t('metadata.nationalityForm.namePlaceholder')}
                disabled={loading}
                style={reqErrors.name ? { borderColor: 'var(--red)' } : undefined}
                aria-invalid={reqErrors.name ? true : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  clearReqError('name');
                }}
              />
              {reqErrors.name ? <div style={FIELD_ERR_STYLE}>{reqErrors.name}</div> : null}
            </div>

            {isEdit ? (
              <div className="al-f">
                <div className="al-l">{t('metadata.nationalities.nationalityCode')}</div>
                <input
                  className="al-inp"
                  value={code}
                  readOnly
                  disabled
                  style={READONLY_FIELD_STYLE}
                />
              </div>
            ) : null}

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <div className="al-l">{t('metadata.nationalities.description')}</div>
              <textarea
                id="nationality-description"
                className="al-inp"
                style={{
                  height: 90,
                  paddingTop: 10,
                  paddingBottom: 10,
                  resize: 'vertical',
                  ...(reqErrors.description ? { borderColor: 'var(--red)' } : {}),
                }}
                value={description}
                maxLength={NATIONALITY_DESCRIPTION_MAX_LENGTH}
                placeholder={t('metadata.nationalities.descriptionPlaceholder')}
                disabled={loading}
                aria-invalid={reqErrors.description ? true : undefined}
                onChange={(e) => {
                  setDescription(e.target.value);
                  clearReqError('description');
                }}
              />
              {reqErrors.description ? <div style={FIELD_ERR_STYLE}>{reqErrors.description}</div> : null}
            </div>

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <label className="ccr-chk" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={active}
                  disabled={loading}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('common.active')}</span>
              </label>
            </div>
          </div>
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('metadata.nationalityForm.cancelConfirm')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/metadata/nationalities');
        }}
      />
    </>
  );
}
